import { Table, Box, Group, Tooltip, ActionIcon } from "@mantine/core";
import IconInfoCircle from "@tabler/icons-react/dist/esm/icons/IconInfoCircle.mjs";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";
import classes from "./rulesTable.module.scss";
import {
  clearRules,
  useSelectRulesData,
} from "../../../../redux/features/rules/rulesSlice";
import { useDispatch } from "react-redux";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import SortIcon from "../../../../components/searchOptions/SortIcon";
import { useEffect, useState } from "react";
import useRulesTable from "./useRulesTable.logic";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import dayjs from "dayjs";
import { useDisclosure } from "@mantine/hooks";
import { DeleteConfirmModal } from "../../../../components/DeleteConfirmationModal";
import { toast } from "react-toastify";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import { TableSkeletonRules } from "../../../../components/Skeletons/TableSkeletonRules";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { dateFormat } from "../../../../utils/dateFormat";

const RulesTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = useSelectRulesData();
  const [deleteId, setDeleteId] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);

  // ── Column definitions ────────────────────────────────────────────────────────
  const COLUMNS = [
    { key: "author", label: t("rules.author"), sortKey: "AUTHOR" },
    { key: "ruleName", label: t("rules.ruleName"), sortKey: "RULE_NAME" },
    { key: "typeOfRule", label: t("rules.ruleType"), sortKey: "RULE_TYPE" },
    { key: "status", label: t("rules.status"), sortKey: "STATUS" },
  ];

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    open();
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    close();
  };

  const {
    take,
    page,
    totalCount,
    sort,
    filters,
    errorRulesFetch,
    isFetchingRules,
    deleteHandler,
    toggleSort,
    handleFilterChange,
    handlePageChange,
    handleTakeChange,
  } = useRulesTable();

  useEffect(() => {
    return () => {
      dispatch(clearRules());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data.map((row, i) => (
    <Table.Tr onClick={() => onRowClick(row.id)} key={`${row.id}-${i}`}>
      <Table.Td>{dayjs(row.dateCreated).format(dateFormat)}</Table.Td>
      <Table.Td>{row?.author?.name}</Table.Td>
      <Tooltip label={row.name} disabled={!row.name}>
        <Table.Td>{row.name}</Table.Td>
      </Tooltip>
      <Table.Td>{row.typeOfRule}</Table.Td>
      <Table.Td>{row.status}</Table.Td>
      <Table.Td>
        <Group gap="md" wrap="nowrap" justify="flex-end">
          <ActionIcon
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteModal(row.id);
            }}
            c="gray"
            variant="transparent"
          >
            <IconTrash />
          </ActionIcon>

          <Tooltip label={row.description || "No description"}>
            <IconInfoCircle color="gray" />
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (errorRulesFetch) {
    toast.error("Error fetching rules", { toastId: 2 });
  }

  const onRowClick = (ruleId) => {
    navigate(`/rules/rule-dashboard/${ruleId}`);
  };

  return (
    <>
      <Box className="table-panel">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["rules-table"]}
        >
          {/* ── Header row ── */}
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group>
                  <Group
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSort("DATE")}
                    gap={4}
                    wrap="nowrap"
                  >
                    {t("rules.date")}
                    <SortIcon col={"DATE"} sort={sort} />
                  </Group>
                  <DateSearchFilter
                    placeholder={t("rules.date")}
                    onChange={(dates) => handleFilterChange("DATE", dates)}
                  />
                </Group>
              </Table.Th>

              {COLUMNS.map((col) => (
                <Table.Th key={col.key}>
                  <Group>
                    <Group
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSort(col.sortKey)}
                      gap={4}
                      wrap="nowrap"
                    >
                      {col.label}
                      <SortIcon col={col.sortKey} sort={sort} />
                    </Group>
                    <ThrottledTextInput
                      size="xs"
                      w={120}
                      delay={600}
                      value={filters[col.key]}
                      onThrottledChange={(val) =>
                        handleFilterChange(col.key, val)
                      }
                    />
                  </Group>
                </Table.Th>
              ))}
              {/* Empty th for action column */}
              <Table.Th style={{ width: 100 }} />
            </Table.Tr>
          </Table.Thead>

          {data.length === 0 && !isFetchingRules ? (
            <Table.Tbody>
              <TableNoResults colSpan={COLUMNS.length + 1} />
            </Table.Tbody>
          ) : (
            <RenderTableWithSkeleton />
          )}
        </Table>
        <DeleteConfirmModal
          onClose={handleCloseDeleteModal}
          opened={opened}
          onDelete={() => deleteHandler(deleteId)}
          label={"Are you sure that you want to delete this rule?"}
        />
      </Box>
      {totalCount > 0 && (
        <AppPagination
          onPageChange={handlePageChange}
          onTakeChange={handleTakeChange}
          page={page}
          take={take}
          total={totalCount}
        />
      )}
    </>
  );

  function RenderTableWithSkeleton() {
    if (isFetchingRules) {
      return <TableSkeletonRules length={take} />;
    }

    return <Table.Tbody pos="relative">{rows}</Table.Tbody>;
  }
};

export default RulesTable;
